import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Tab, Tabs, Typography } from 'components/mui';
import { useNotificationProvider } from 'hooks';
import { SyncSessionLists } from 'api/lotusBlossom/lotusBlossomAPI';
import NextSessionHeader from 'containers/pages/QuantumHealing/components/NextSessionHeader';
import ParticipantsGrid from 'containers/pages/QuantumHealing/components/ParticipantsGrid';
import ComingUpSessions from 'containers/pages/QuantumHealing/components/ComingUpSessions';
import PastSessionsPanel from 'containers/pages/QuantumHealing/components/PastSessionsPanel';
import ClientsPanel from 'containers/pages/QuantumHealing/components/ClientsPanel';
import useQuantumHealingSession from 'containers/pages/QuantumHealing/hooks/useQuantumHealingSession';
import useLotusBlossomPurchases from 'containers/pages/QuantumHealing/hooks/useLotusBlossomPurchases';
import useQuantumHealingAccess from 'containers/pages/QuantumHealing/hooks/useQuantumHealingAccess';

type TabValue = 'upcoming' | 'past' | 'clients';

const SUBTITLES: Record<TabValue, string> = {
  upcoming:
    'Everyone attending your current and upcoming sessions — members and one-time bookings.',
  past: 'Who attended a previous session — members and one-time bookings.',
  clients: 'Everyone who has taken part, with their purchase history.',
};

/**
 * @component QuantumHealingParticipants
 * @description Route entry for /quantum-healing-participants. Checks access
 * before mounting the page, so a denied user triggers none of its requests.
 * @returns {JSX.Element | null}
 */
function QuantumHealingParticipants() {
  const { allowed } = useQuantumHealingAccess();
  return allowed ? <QuantumHealingParticipantsPage /> : null;
}

/**
 * Resolves the sessions once and shares them across the tabs.
 */
function QuantumHealingParticipantsPage() {
  const {
    state,
    current,
    upcoming,
    sessions,
    endDatesKnown,
    subscribersListId,
    participantsListId,
    reload,
  } = useQuantumHealingSession();
  // Read once here rather than per tab: it pages Stripe, which is slow.
  const { purchases, updateMembership } = useLotusBlossomPurchases();

  // The top of the tab is the session running now — for its whole length — or,
  // between sessions, the next one. Everything after it is listed underneath.
  const featured = current[0] ?? upcoming[0] ?? null;
  const comingUp = useMemo(
    () => [...current, ...upcoming].filter((s) => s !== featured),
    [current, upcoming, featured]
  );
  const [tab, setTab] = useState<TabValue>('upcoming');
  const [syncing, setSyncing] = useState(false);
  const { notifySuccess, notifyWarning, notifyError } = useNotificationProvider();

  /**
   * Sessions exist here only as mail lists, and only a booking creates one — so
   * a newly published month, or one every attendee is a member for, has nothing
   * to show until this runs. Deliberately a button: it writes, and loading a
   * page should not.
   */
  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      const { created, synced, failed = [] } = await SyncSessionLists();
      const done = `Added ${created} session${created === 1 ? '' : 's'} and ${synced} member seat${synced === 1 ? '' : 's'}.`;

      if (failed.length > 0) {
        // The rest went through; running it again retries only what's left.
        console.error('[QuantumHealing] SyncSessionLists failed for:', failed);
        notifyWarning(
          `${done} ${failed.length} session${failed.length === 1 ? '' : 's'} couldn't be synced — please try again.`
        );
      } else {
        notifySuccess(created === 0 && synced === 0 ? 'Sessions are already up to date.' : done);
      }
      reload();
    } catch (error) {
      console.error('[QuantumHealing] SyncSessionLists:', error);
      notifyError('Could not sync sessions. Please try again.');
    } finally {
      setSyncing(false);
    }
  }, [notifySuccess, notifyWarning, notifyError, reload]);

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Quantum Healing Participants
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 2, fontWeight: 400 }}
          >
            {SUBTITLES[tab]}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          onClick={handleSync}
          disabled={syncing}
          sx={{ flexShrink: 0, mt: 0.5 }}
        >
          {syncing ? 'Syncing…' : 'Sync sessions'}
        </Button>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, next: TabValue) => setTab(next)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="upcoming" label="Next Sessions" />
        <Tab value="past" label="Past Sessions" />
        <Tab value="clients" label="Clients" />
      </Tabs>

      {tab === 'upcoming' && (
        <>
          <NextSessionHeader
            state={state}
            session={featured}
            isCurrent={featured !== null && current.includes(featured)}
            endDatesKnown={endDatesKnown}
          />

          {state === 'ready' && featured && (
            <ParticipantsGrid
              // Remount when the featured session changes, so the previous
              // roster isn't left on screen while the new one loads.
              key={featured.listId}
              session={featured}
              subscribersListId={subscribersListId}
              purchases={purchases}
            />
          )}

          {state === 'ready' && (
            <ComingUpSessions
              sessions={comingUp}
              subscribersListId={subscribersListId}
              purchases={purchases}
            />
          )}
        </>
      )}

      {tab !== 'upcoming' && (
        <>
          {state === 'loading' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {state === 'error' && (
            <Alert severity="error">
              We couldn&apos;t load your sessions. Please refresh the page — if it
              keeps happening, contact support.
            </Alert>
          )}

          {state === 'ready' && tab === 'past' && (
            <PastSessionsPanel
              sessions={sessions}
              subscribersListId={subscribersListId}
              purchases={purchases}
            />
          )}

          {state === 'ready' && tab === 'clients' && (
            <ClientsPanel
              participantsListId={participantsListId}
              purchases={purchases}
              onMembershipChange={updateMembership}
            />
          )}
        </>
      )}
    </Box>
  );
}

export default QuantumHealingParticipants;
